import { CheckCircle2, XCircle, HelpCircle, Eye, Settings, TrendingUp, Target, BookOpen, LogOut } from "lucide-react";
import { HalfCircle } from "@/components/ui/HalfCircle";
import { ReportCard } from "./ReportCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ReportTable";

type Score = "yes" | "partially" | "no" | "insufficient";

interface TradeoffRow {
  criterion: string;
  score: Score;
  gain: string;
  giveUp: string;
}

interface TradeoffTableProps {
  tradeoffs: TradeoffRow[];
}

const CRITERION_ICONS: Record<string, React.ReactNode> = {
  SEE: <Eye className="w-4 h-4" style={{ color: "#4DA6D4" }} />,
  CHANGE: <Settings className="w-4 h-4" style={{ color: "#9B4FD4" }} />,
  ADAPT: <TrendingUp className="w-4 h-4" style={{ color: "#5BBFB5" }} />,
  USE: <Target className="w-4 h-4" style={{ color: "#C47B9F" }} />,
  LEARN: <BookOpen className="w-4 h-4" style={{ color: "#5C6BC0" }} />,
  EXIT: <LogOut className="w-4 h-4" style={{ color: "#C4785A" }} />,
};

export function TradeoffTable({ tradeoffs }: TradeoffTableProps) {
  const scoreConfig = {
    yes: {
      icon: CheckCircle2,
      label: "Met",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    partially: {
      icon: ({ className }: { className?: string }) => <HalfCircle className={className} />,
      label: "Partially Met",
      color: "text-amber-600",
      bgColor: "bg-amber-50"
    },
    no: {
      icon: XCircle,
      label: "Not Met",
      color: "text-rose-600",
      bgColor: "bg-rose-50"
    },
    insufficient: {
      icon: HelpCircle,
      label: "Insufficient Info",
      color: "text-slate-600",
      bgColor: "bg-slate-50"
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-6">
        <h2 className="text-3xl font-sans font-semibold text-[#1e3a5f] mb-2">Trade-off Summary</h2>
        <div className="h-1 w-20 bg-[#d4975c] rounded-full" />
      </div>

      <ReportCard className="overflow-hidden border border-[#e8e3dc]">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#f0ebe4]">
              <TableHead className="font-semibold text-[#1e3a5f] w-1/6">Criterion</TableHead>
              <TableHead className="font-semibold text-[#1e3a5f] w-[140px]">Score</TableHead>
              <TableHead className="font-semibold text-[#1e3a5f]">What You Gain</TableHead>
              <TableHead className="font-semibold text-[#1e3a5f]">What You Give Up</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tradeoffs.map((row, index) => {
              const config = scoreConfig[row.score];
              const ScoreIcon = config.icon;
              const criterionIcon = CRITERION_ICONS[row.criterion];
              
              return (
                <TableRow key={index} className="hover:bg-[#faf8f5]">
                  <TableCell className="font-semibold text-[#1e3a5f]">
                    <div className="flex items-center gap-2.5">
                      {criterionIcon && (
                        <span className="flex-shrink-0">{criterionIcon}</span>
                      )}
                      {row.criterion}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bgColor}`}>
                      <ScoreIcon className={`w-4 h-4 ${config.color}`} />
                      <span className={`text-xs font-semibold ${config.color}`}>{config.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {row.gain || <span className="text-slate-400 italic">&mdash;</span>}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {row.giveUp || <span className="text-slate-400 italic">&mdash;</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ReportCard>
    </div>
  );
}
